import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Check, Instagram, Loader2, PartyPopper, QrCode, ShoppingBag } from "lucide-react";
import { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DeliveryEstimateCard } from "@/components/ds/DeliveryEstimateCard";
import { toast } from "sonner";

const publicApi = axios.create({ baseURL: API });

const dataHora = (iso) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

export default function OrderTracking() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pixBusy, setPixBusy] = useState(false);

  // Clique repetido não gera cobrança nova: o backend reaproveita a cobrança pendente.
  const payPix = async () => {
    if (pixBusy) return;
    setPixBusy(true);
    try {
      const { data: charge } = await publicApi.post(`/public/orders/${token}/pix-charge`);
      window.location.assign(`${charge.path}?pedido=${token}`);
    } catch (e) {
      toast.error(apiError(e));
      setPixBusy(false);
    }
  };

  const decide = async (action) => {
    const note =
      action === "request-change"
        ? window.prompt("O que você gostaria de mudar?") || ""
        : "";
    if (action === "request-change" && note === null) return;
    setBusy(true);
    try {
      const { data: d } = await publicApi.post(
        `/public/orders/${token}/customization/${action}`,
        { note }
      );
      setData(d);
      toast.success(
        action === "approve"
          ? "Personalização aprovada! Já vamos começar a produção."
          : "Pedido de alteração enviado. Vamos te chamar para ajustar."
      );
    } catch (e) {
      toast.error("Não foi possível registrar. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    publicApi
      .get(`/public/orders/${token}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setError("Não encontramos este pedido. Confira o link recebido."));
  }, [token]);

  if (error)
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <p className="text-center text-sm text-muted-foreground" data-testid="tracking-error">
          {error}
        </p>
      </div>
    );

  if (!data)
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );

  const done = data.status === "ENTREGUE";

  return (
    <div className="min-h-screen bg-background px-5 py-10" data-testid="order-tracking-page">
      <div className="mx-auto w-full max-w-lg space-y-6">
        <header className="text-left">
          <img
            src="/brand/tr3nd-logo.jpg"
            alt={data.studio_name}
            data-testid="tracking-logo"
            className="h-14 w-14 rounded-lg object-contain"
          />
          <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
            Acompanhe seu pedido
          </h1>
          <p className="mt-2 font-num text-sm text-muted-foreground" data-testid="tracking-number">
            Pedido #{data.number}
            {data.customer_first_name ? ` · ${data.customer_first_name}` : ""}
          </p>
          {data.created_at && (
            <p className="font-num text-xs text-muted-foreground">
              Feito em {dataHora(data.created_at)}
            </p>
          )}
          {data.receiving_method && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="tracking-receiving">
              {data.receiving_method === "RETIRADA"
                ? "Retirada · confirmaremos o horário pelo WhatsApp."
                : `Entrega · ${data.shipping_preference || "a combinar"} · o custo da entrega será confirmado pelo WhatsApp.`}
            </p>
          )}
        </header>

        <div
          className={`rounded-2xl border p-5 ${
            data.cancelled
              ? "border-destructive/40 bg-destructive/10"
              : "border-brand/30 bg-brand/10"
          }`}
          data-testid="tracking-current-status"
        >
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Status atual
          </p>
          <p className="mt-1 font-display text-2xl font-medium">{data.status_label}</p>
        </div>

        {!data.cancelled && (
          <div
            className={`flex flex-wrap items-center gap-2 rounded-2xl border p-4 ${
              data.payment_status === "CONFIRMADO"
                ? "border-emerald-500/40 bg-emerald-500/10"
                : "border-amber-500/40 bg-amber-500/10"
            }`}
            data-testid="tracking-payment-status"
          >
            <span
              className={`text-sm font-medium ${
                data.payment_status === "CONFIRMADO" ? "text-emerald-300" : "text-amber-200"
              }`}
            >
              {data.payment_status === "CONFIRMADO"
                ? "✓ Pagamento confirmado"
                : "⏳ Pagamento aguardando"}
            </span>
            {data.payment_method && (
              <span className="text-xs text-muted-foreground">· {data.payment_method}</span>
            )}
            {data.payment_status !== "CONFIRMADO" && data.payment_claimed_at && (
              <span className="w-full text-xs text-muted-foreground">
                Recebemos seu aviso de pagamento e estamos conferindo.
              </span>
            )}
          </div>
        )}

        {!data.cancelled && data.can_pay_pix && (
          <div
            className="rounded-2xl border border-white/10 bg-card p-5"
            data-testid="tracking-pix-offer"
          >
            <p className="font-display text-base font-medium">
              Mudou de ideia sobre o pagamento?
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Você pode pagar agora pelo Pix, sem precisar esperar a retirada do pedido.
            </p>
            <Button
              onClick={payPix}
              disabled={pixBusy}
              data-testid="tracking-pay-pix-btn"
              className="mt-4 h-12 w-full gap-2 rounded-full font-semibold transition-transform active:scale-95"
            >
              {pixBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
              Pagar agora com Pix
            </Button>
          </div>
        )}

        {!data.cancelled && data.delivery_estimate && (
          <DeliveryEstimateCard
            estimate={data.delivery_estimate}
            testid="tracking-delivery-estimate"
          />
        )}

        {!data.cancelled && (
          <ol className="space-y-1 rounded-2xl border border-white/10 bg-card p-5" data-testid="tracking-timeline">
            {data.steps.map((s) => (
              <li
                key={s.key}
                className="flex items-center gap-3 py-2"
                data-testid={`tracking-step-${s.key.toLowerCase()}`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[10px] ${
                    s.state === "done"
                      ? "border-emerald-500/50 bg-emerald-500/20 text-emerald-300"
                      : s.state === "current"
                      ? "border-brand bg-brand/20 text-brand"
                      : "border-white/15 text-muted-foreground"
                  }`}
                >
                  {s.state === "done" ? <Check className="h-3.5 w-3.5" /> : ""}
                </span>
                <span
                  className={`text-sm ${
                    s.state === "current"
                      ? "font-medium text-brand"
                      : s.state === "done"
                      ? "text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {s.label}
                </span>
              </li>
            ))}
          </ol>
        )}

        {data.items.length > 0 && (
          <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="tracking-items">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Itens do pedido
            </p>
            <ul className="mt-3 space-y-2">
              {data.items.map((it, i) => (
                <li key={i} className="text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="min-w-0 truncate">
                      <span className="font-num">{it.quantity}x</span> {it.name}
                    </span>
                    <span
                      className={`shrink-0 text-xs ${
                        it.ready ? "text-emerald-300" : "text-brand"
                      }`}
                    >
                      {it.ready ? "Pronto" : "Em produção"}
                    </span>
                  </div>
                  {it.customization?.length > 0 && (
                    <ul
                      className="mt-1.5 space-y-0.5 rounded-lg border border-brand/25 bg-brand/5 p-2.5"
                      data-testid={`tracking-customization-${i}`}
                    >
                      <li className="text-[10px] font-bold uppercase tracking-wide text-brand">
                        Personalização
                      </li>
                      {it.customization.map((c) => (
                        <li key={c.name} className="text-xs text-muted-foreground">
                          {c.name}: <span className="text-foreground">{c.value}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.customization_status && !data.cancelled && (
          <div
            className={`rounded-2xl border p-5 ${
              data.customization_status === "APROVADA"
                ? "border-emerald-500/30 bg-emerald-500/10"
                : "border-amber-500/40 bg-amber-500/10"
            }`}
            data-testid="tracking-customization-block"
          >
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Personalização
            </p>
            <p className="mt-1 font-display text-lg">
              {data.customization_status === "APROVADA"
                ? "✅ Aprovada por você"
                : data.customization_label}
            </p>
            {data.customization_status === "APROVADA" ? (
              data.customization_approved_at && (
                <p className="mt-1 font-num text-xs text-muted-foreground">
                  {dataHora(data.customization_approved_at)}
                </p>
              )
            ) : (
              <>
                <p className="mt-2 text-sm text-muted-foreground">
                  Confira as informações da sua personalização acima. A produção será iniciada
                  somente após sua aprovação.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  <Button
                    onClick={() => decide("approve")}
                    disabled={busy}
                    data-testid="approve-customization-btn"
                    className="h-12 w-full rounded-full font-semibold"
                  >
                    Aprovar personalização
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => decide("request-change")}
                    disabled={busy}
                    data-testid="request-change-btn"
                    className="h-11 w-full rounded-full text-xs"
                  >
                    Solicitar alteração
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {done && (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center" data-testid="tracking-done-block">
            <PartyPopper className="mx-auto h-6 w-6 text-emerald-300" />
            <p className="mt-2 font-display text-xl font-medium">Pedido concluído!</p>
            <p className="mt-1 text-sm text-muted-foreground">Obrigado pela sua compra.</p>
            <p className="mt-4 text-sm">
              Gostou? Conheça mais produtos da {data.studio_name}.
            </p>
          </div>
        )}

        {data.suggestions?.length > 0 && (
          <div data-testid="tracking-suggestions">
            <p className="font-display text-lg font-medium">Você também pode gostar</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {data.suggestions.map((s) => (
                <a
                  key={s.id}
                  href={`/catalogo?busca=${encodeURIComponent(s.name)}`}
                  className="group overflow-hidden rounded-xl border border-white/10 bg-card transition-colors hover:border-brand/40"
                  data-testid={`tracking-suggestion-${s.id}`}
                >
                  <div className="aspect-square overflow-hidden bg-secondary/40">
                    {s.photo_url ? (
                      <img
                        src={s.photo_url}
                        alt={s.name}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center">
                        <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="line-clamp-2 text-[11px] leading-tight">{s.name}</p>
                    <p className="mt-1 font-num text-xs text-brand">
                      {s.price.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                    </p>
                    <span className="mt-1 block text-[10px] text-muted-foreground group-hover:text-brand">
                      Ver produto
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button asChild className="h-11 flex-1 gap-2 rounded-full font-semibold">
            <a href="/catalogo" data-testid="tracking-catalog-btn">
              <ShoppingBag className="h-4 w-4" /> Voltar ao catálogo
            </a>
          </Button>
          {data.instagram && (
            <Button asChild variant="outline" className="h-11 flex-1 gap-2 rounded-full">
              <a
                href={data.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="tracking-instagram-btn"
              >
                <Instagram className="h-4 w-4" /> Instagram
              </a>
            </Button>
          )}
        </div>

        <p className="pb-6 text-center text-[11px] text-muted-foreground">
          Esta página atualiza sozinha conforme seu pedido avança. Guarde o link.
        </p>
      </div>
    </div>
  );
}
